import { Button, CircularProgress, Dialog, IconButton, Stack, SvgIcon, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from "react-i18next";
import { getStyles } from "./styles";
import { tokens } from "../../../../../../../locales/tokens";
import { TextAutocomplete } from "../../../../../../../ui-kit/textAutocomplete";
import AddIcon from '@mui/icons-material/Add';
import { ImagePicker } from "../imagePicker";
import { useQuestionCreate } from "../../../presenters/useQuestionCreate";
import { RepliesList } from "../repliesList";
import { RepliesDraggableList } from "../repliesDraggableList";
import { TextInputModal } from "../textInputModal";

interface IProps {
    testId: any;
    isOpen: boolean;
    onClose: () => void;
    currentQuestion: any;
}; 

export const QuestionModal: FC<IProps> = ({ isOpen, onClose, currentQuestion, testId }) => {
    const { t } = useTranslation();
    const styles = useMemo(() => getStyles(), []);
    const { replies, typeOptions, createOrEditQuestion, type, typeValid, changeType, title, titleValid, onChangeTitle, addReply, removeReply, onChangeCorrectReply,
        questionPoints, questionPointsValid, onChangeQuestionPoints, handleFileChange, imageSrc, handleClearImageClick, onChangeReply, setReplies, closeAndClearData,
        questionId, repliesValid, setRepliesValid, createQuestionClick, editQuestionClick, fileInputRef } = useQuestionCreate(onClose, currentQuestion, testId);

    const answersList = useMemo(() => {
        if (type?.value === 'Single' || type?.value === 'Multiple') {
            return (
                <RepliesList
                    type={type?.value}
                    replies={replies}
                    repliesValid={repliesValid}
                    removeReply={removeReply}
                    onChangeReply={onChangeReply}
                    onChangeCorrectReply={onChangeCorrectReply}
                />
            );
        } else if (type?.value === 'Order') {
            return (
                <RepliesDraggableList
                    testId={testId}
                    replies={replies}
                    repliesValid={repliesValid}
                    setReplies={setReplies}
                    setRepliesValid={setRepliesValid}
                    removeReply={removeReply}
                    onChangeReply={onChangeReply}
                />
            );
        } else {
            return null;
        }
    }, [type, replies, repliesValid]);

    return (
        <Dialog open={isOpen} onClose={closeAndClearData} maxWidth='md' fullWidth sx={{ borderRadius: '20px' }}>
            <Stack m={4} sx={styles.wrapper}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        {currentQuestion ? t(tokens.test.editQuestion) : t(tokens.test.createQuestion)}
                    </Typography>
                    <IconButton onClick={closeAndClearData}>
                        <SvgIcon><ClearIcon /></SvgIcon>
                    </IconButton>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mt={3} spacing={3}>
                    <Stack spacing={2} width="100%">
                        <Stack width="100%">
                            <Typography mb={1} color={typeValid ? 'inherit' : 'error'}>
                                {t(tokens.test.type)}*
                            </Typography>
                            <TextAutocomplete
                                title={t(tokens.documents.type) + '*'}
                                value={type}
                                handleChangeValue={changeType}
                                optionList={typeOptions}
                                isMultiline={false}
                            />
                        </Stack>
                        <TextInputModal
                            title={t(tokens.test.title) + '*'}
                            label={t(tokens.test.title)}
                            isValid={titleValid}
                            value={title}
                            onChangeValue={onChangeTitle}
                        />
                        {/* <TextInputModal
                            title={t(tokens.test.allPoints) + '*'}
                            label={t(tokens.test.allPoints)}
                            isValid={questionPointsValid}
                            value={questionPoints}
                            onChangeValue={onChangeQuestionPoints}
                        /> */}
                    </Stack>
                    <Stack width="70%" alignItems="center">
                        <ImagePicker
                            fileInputRef={fileInputRef}
                            imageSrc={imageSrc}
                            handleFileChange={handleFileChange}
                            handleClearImageClick={handleClearImageClick}
                        />
                    </Stack>
                </Stack>
                <Stack pt={2} alignItems="flex-start">
                    <Button
                        startIcon={<AddIcon />}
                        sx={styles.addQuestionButton}
                        onClick={addReply}
                        disabled={!(!!questionId) || type?.value === 'Free'}
                    >
                        {t(tokens.test.addReply)}
                    </Button>
                    {(!questionId && type?.value !== 'Free') &&
                        <Stack position="relative" left="25px">
                            <Typography variant="caption" color="#F1594E">
                                {t(tokens.test.message.needQuestionForReplies)}
                            </Typography>
                        </Stack>}
                </Stack>
                {replies.length > 0 &&
                    <Stack mt={2}>
                        {answersList}
                    </Stack>
                }
                <Stack direction='row' mt={3} spacing={3} pt={3}>
                    <Button
                        variant="contained"
                        sx={styles.createButton}
                        onClick={createOrEditQuestion}
                        disabled={createQuestionClick || editQuestionClick}
                    >
                        {createQuestionClick || editQuestionClick
                            ? <CircularProgress size={15} />
                            :
                            questionId ? t(tokens.common.save) : t(tokens.common.create)}
                    </Button>
                    <Button
                        variant="outlined"
                        sx={styles.cancelButton}
                        onClick={closeAndClearData}
                        disabled={createQuestionClick || editQuestionClick}
                    >
                        {t(tokens.common.cancel)}
                    </Button>
                </Stack>
            </Stack>
        </Dialog>
    )
};